import { useCallback, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { isGameSeed, type ConversationSeed, type EnglishIntensity } from '@orborix/shared-types';
import type { UseVoiceSessionReturn } from '@orborix/voice-client';
import { useViewNavigate } from '../../../hooks/useViewNavigate.js';
import { pickSecretWord, type SecretWord } from '../controls/secretWords.js';
import { useSfx } from '../../../lib/sfx/index.js';
import { track } from '../../../lib/analytics.js';

export interface UseSeedMenuReturn {
  seedMenuOpen: boolean;
  activeSeed: ConversationSeed | null;
  /** Razón por la que el menú está inerte (programa/escenario/examen), o null. */
  seedDisabledReason: string | null;
  openSeedMenu: () => void;
  closeSeedMenu: () => void;
  selectSeed: (seed: ConversationSeed) => void;
  clearSeed: () => void;
  /** Dirección "Noticias" (news-service Fase 5): el coach trae una noticia real
   *  de la DB. Re-tocar = otra noticia (re-roll). */
  selectNews: () => void;
  goToScenarios: () => void;
  /**
   * Palabra secreta del juego "Palabra secreta" (`describe-it`). CLIENT-ONLY
   * por diseño: se muestra en pantalla y JAMÁS viaja al backend — el coach no
   * la conoce y la adivina en serio. `null` fuera del juego.
   */
  secretWord: SecretWord | null;
  /** Nueva palabra + re-envío del seed (el re-pivote del coach abre otra ronda). */
  rerollSecretWord: () => void;
}

/**
 * Semilla de conversación (menú "¿De qué hablamos?", modo libre). Dirección
 * efímera (per-sesión, NO se persiste): viaja por `sendSessionUpdate({ seed })`
 * → el backend re-bakea el FOCO DE LA CHARLA y dispara un pivote activo del
 * coach. Sólo aplica en charla libre PURA.
 *
 * Incluye los JUEGOS (`GAME_SEEDS`): misma mecánica de seed. El único con
 * estado extra del cliente es `describe-it` (la palabra secreta de arriba).
 */
export function useSeedMenu(args: {
  sessionRef: RefObject<UseVoiceSessionReturn | null>;
  examMode: boolean;
  inProgram: boolean;
  inScenario: boolean;
  /** Intensidad efectiva de la sesión — calibra el banco de palabras secretas. */
  intensity: EnglishIntensity;
  /**
   * Salida ordenada de la pantalla (guarda del back de la conversación, ver
   * `useHistoryBackGuard`): "Ir a escenarios" abandona la charla, así que la
   * navegación pasa por acá para no dejar el centinela huérfano. Opcional:
   * sin ella navega directo (tests / contextos sin guarda).
   */
  exitScreen?: (then: () => void) => void;
}): UseSeedMenuReturn {
  const { sessionRef, examMode, inProgram, inScenario, intensity, exitScreen } = args;
  const navigate = useViewNavigate();

  const [seedMenuOpen, setSeedMenuOpen] = useState(false);
  const [activeSeed, setActiveSeed] = useState<ConversationSeed | null>(null);
  const [secretWord, setSecretWord] = useState<SecretWord | null>(null);
  // Palabras ya jugadas esta sesión (para no repetir en rondas seguidas).
  const recentWordsRef = useRef<string[]>([]);
  const seedDisabledReason: string | null = examMode
    ? 'El menú de temas es para la charla libre. Ahora estás en el examen de nivelación.'
    : inProgram
      ? 'Estás en una sesión del programa: el tema lo marca la unidad.'
      : inScenario
        ? 'Estás en un roleplay: el tema lo marca la situación.'
        : null;

  const { play } = useSfx();

  const dealSecretWord = useCallback((): void => {
    const word = pickSecretWord(intensity, recentWordsRef.current);
    recentWordsRef.current = [...recentWordsRef.current, word.en].slice(-12);
    setSecretWord(word);
  }, [intensity]);

  const openSeedMenu = useCallback((): void => setSeedMenuOpen(true), []);
  const closeSeedMenu = useCallback((): void => setSeedMenuOpen(false), []);
  const selectSeed = useCallback(
    (seed: ConversationSeed): void => {
      setActiveSeed(seed);
      // Palabra secreta: se reparte ANTES de avisar al backend, pero el update
      // sólo lleva el seed — la palabra nunca sale del cliente (regla del juego).
      if (seed === 'describe-it') {
        dealSecretWord();
        play('secret-word-deal');
      } else setSecretWord(null);
      sessionRef.current?.sendSessionUpdate({ seed });
      if (isGameSeed(seed)) track('game_started', { game: seed });
      setSeedMenuOpen(false);
    },
    [sessionRef, dealSecretWord, play],
  );
  const clearSeed = useCallback((): void => {
    setActiveSeed(null);
    setSecretWord(null);
    sessionRef.current?.sendSessionUpdate({ seed: null });
    setSeedMenuOpen(false);
  }, [sessionRef]);
  // Noticias (news-service Fase 5): no es un seed (no hay chip persistente).
  // Limpia el seed activo y pide al backend pivotar a una noticia fresca. El
  // runtime de languages elige el artículo; re-tocar = re-roll a otra.
  const selectNews = useCallback((): void => {
    setActiveSeed(null);
    setSecretWord(null);
    sessionRef.current?.sendSessionUpdate({ news: true });
    setSeedMenuOpen(false);
  }, [sessionRef]);
  const goToScenarios = useCallback((): void => {
    setSeedMenuOpen(false);
    // La charla termina al irse (el unmount cierra la sesión): la entrada de la
    // conversación se REEMPLAZA para que back desde escenarios no la remonte.
    const go = (): void => void navigate('/scenarios', { replace: true });
    if (exitScreen) exitScreen(go);
    else go();
  }, [navigate, exitScreen]);

  // Nueva ronda de "Palabra secreta": reparte otra palabra y RE-ENVÍA el seed —
  // el backend re-pivotea a propósito con el mismo seed (canal de "nueva
  // ronda": el coach arranca de nuevo sin arrastrar las pistas viejas).
  const rerollSecretWord = useCallback((): void => {
    dealSecretWord();
    play('secret-word-reveal');
    sessionRef.current?.sendSessionUpdate({ seed: 'describe-it' });
  }, [sessionRef, dealSecretWord, play]);

  return {
    seedMenuOpen,
    activeSeed,
    seedDisabledReason,
    openSeedMenu,
    closeSeedMenu,
    selectSeed,
    clearSeed,
    selectNews,
    goToScenarios,
    secretWord,
    rerollSecretWord,
  };
}
